import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ShopHeader from '@/components/commerce/ShopHeader';
import ShopFooter from '@/components/commerce/ShopFooter';
import MobileBottomNav from '@/components/commerce/MobileBottomNav';
import CartDrawer from '@/components/commerce/CartDrawer';
import { useShopOrg } from '@/context/ShopOrgContext';
import { useShopCustomerAuth } from '@/context/ShopCustomerAuthContext';
import { LogOut, Package, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUS_LABELS = {
  nieuw: 'Nieuw',
  betaald: 'Betaald',
  verzonden: 'Verzonden',
  afgeleverd: 'Afgeleverd',
  geannuleerd: 'Geannuleerd',
  terugbetaald: 'Terugbetaald',
};

const EMPTY_LOGIN_FORM = { email: '', password: '' };
const EMPTY_REGISTER_FORM = { name: '', email: '', password: '', phone: '' };

const ShopAccountPage = () => {
  const { id: orgId, name: organizationName, logoUrl, footer } = useShopOrg();
  const { customer, loading, isLoggedIn, login, register, logout, token } = useShopCustomerAuth();

  const [mode, setMode] = useState('login');
  const [loginForm, setLoginForm] = useState(EMPTY_LOGIN_FORM);
  const [registerForm, setRegisterForm] = useState(EMPTY_REGISTER_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const fetchMyOrders = useCallback(async () => {
    if (!token) return;
    try {
      setOrdersLoading(true);
      const response = await axios.get(`${API}/public/commerce/${orgId}/orders/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Kon bestellingen niet laden');
    } finally {
      setOrdersLoading(false);
    }
  }, [orgId, token]);

  useEffect(() => {
    if (isLoggedIn) fetchMyOrders();
  }, [isLoggedIn, fetchMyOrders]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await login(loginForm.email, loginForm.password);
      toast.success('Ingelogd');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Inloggen mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await register(registerForm.name, registerForm.email, registerForm.password, registerForm.phone || null);
      toast.success('Account aangemaakt');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Registreren mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-200"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20 sm:pb-0">
      <Toaster position="top-right" richColors />
      <ShopHeader organizationName={organizationName} logoUrl={logoUrl} showSearch={false} searchValue="" onSearchChange={() => {}} />
      <CartDrawer />

      <main className="max-w-xl mx-auto px-4 py-10">
        {!isLoggedIn ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex gap-2 mb-6">
                <Button
                  variant={mode === 'login' ? 'default' : 'outline'}
                  className={mode === 'login' ? 'rounded-xl bg-teal-600 hover:bg-teal-700 flex-1' : 'rounded-xl flex-1'}
                  onClick={() => setMode('login')}
                >
                  Inloggen
                </Button>
                <Button
                  variant={mode === 'register' ? 'default' : 'outline'}
                  className={mode === 'register' ? 'rounded-xl bg-teal-600 hover:bg-teal-700 flex-1' : 'rounded-xl flex-1'}
                  onClick={() => setMode('register')}
                >
                  Account aanmaken
                </Button>
              </div>

              {mode === 'login' ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">E-mail</Label>
                    <Input id="login-email" type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="login-password">Wachtwoord</Label>
                    <Input id="login-password" type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                  </div>
                  <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700" disabled={submitting}>
                    Inloggen
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="register-name">Naam</Label>
                    <Input id="register-name" required value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="register-email">E-mail</Label>
                    <Input id="register-email" type="email" required value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="register-phone">Telefoon (optioneel)</Label>
                    <Input id="register-phone" value={registerForm.phone} onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="register-password">Wachtwoord</Label>
                    <Input id="register-password" type="password" required minLength={8} value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} />
                    <p className="text-xs text-slate-500 mt-1">Minimaal 8 tekens.</p>
                  </div>
                  <Button type="submit" className="w-full rounded-xl bg-teal-600 hover:bg-teal-700" disabled={submitting}>
                    Account aanmaken
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-teal-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{customer.name}</p>
                    <p className="text-sm text-slate-500">{customer.email}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-xl" onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Uitloggen
                </Button>
              </CardContent>
            </Card>

            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-3">Mijn bestellingen</h2>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                  {ordersLoading ? (
                    <div className="p-6 text-sm text-slate-500">Laden...</div>
                  ) : orders.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                      Je hebt nog geen bestellingen.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {orders.map((order) => (
                        <div key={order.id} className="p-4">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-900">{order.order_number}</span>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                              {ORDER_STATUS_LABELS[order.status] || order.status}
                            </span>
                          </div>
                          <div className="text-sm text-slate-500 mt-1">
                            {order.items.map((item) => `${item.title} x${item.quantity}`).join(', ')}
                          </div>
                          <div className="text-sm font-medium text-slate-900 mt-1">€ {order.total_amount.toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>

      <ShopFooter organizationName={organizationName} footer={footer} />
      <MobileBottomNav />
    </div>
  );
};

export default ShopAccountPage;
